import { z } from "zod";

import {
  DEFAULT_PERMISSION_SET_RESOURCE_RELATION,
  PermissionSetNameSchema,
  PermissionSetResourceRelationSchema,
  PermissionSetSchema
} from "../../../../shared/domain/roles/permission-set.types";
import { PermissionSetMemberSchema } from "../../../../shared/domain/roles/permission-set-member.types";
import { PermissionSetSurfaceValueSchema } from "../../../../shared/domain/roles/permission-set-surface.types";
import { RoleCapabilitySchema } from "../../../../shared/domain/roles/role-capability.types";

/**
 * 🚨 `RoleGroup` (here) AND `RoleGroupGrant` WERE DIFFERENT TABLES ANSWERING
 * DIFFERENT QUESTIONS, one character apart in name. `RoleGroupGrant` — an ORG
 * `UserGroup` reaching ONE surface of a Role, subject always a UserGroup,
 * carrying no capabilities — folded INTO this model: it is now a `RoleGroup`
 * carrying the grant's surfaces with one `group` member row, rather than a
 * schema of its own. The table is historical only (0 live rows) and no route
 * serves it; this file is where that capability lives now.
 *
 * Every route below is a sub-resource of `:id`, so none of it collides with
 * `GET /api/roles/:id`.
 */
export const PermissionSetsListResponseSchema = z.object({
  groups: z.array(PermissionSetSchema)
});

/**
 * The Role and the permission set named on the path.
 *
 * 🚨 `roleGroupId`, NOT `groupId`, AND THE NAME IS LOAD-BEARING TWICE. Adding a
 * permission set to this Role also carries a `group` member whose id is a
 * `UserGroup.id` — a different table behind the same word `groupId` would
 * otherwise suggest. Reusing it here would (a) invite the reader to think the
 * two ids are the same thing and (b) put one name under two verdicts inside
 * one manifest, which `id-validation-consistency.test.ts` rule 2 refuses by
 * name.
 *
 * BOTH ARE UUID-CHECKED. `RoleGroup.id` and `Role.id` are each
 * `String @id @db.Uuid`, so a malformed value cannot be compared to either
 * column at all: left loose it reaches Prisma, raises `P2023`, and renders as a
 * flat 400 naming no field. NEX-3552 tightened every Role path id together,
 * which is the only spelling `id-validation-consistency.test.ts` accepts.
 *
 * This object needs no `id-validation-consistency.test.ts` allowlist entry at
 * all; the one the permission-set family still carries belongs to its membership
 * twin `PermissionSetMemberPathVarsSchema`, whose `userId` is a Clerk id that is
 * never a uuid. `RoleIdShapeGuard` also shape-checks every `:id` on these
 * controllers at runtime and answers 404, and because a guard runs before the
 * handler parses its path vars, that 404 — never the `.uuid()` refusal above —
 * is what a malformed `:id` actually gets here.
 */
export const PermissionSetPathVarsSchema = z.object({
  id: z.string().uuid(),
  roleGroupId: z.string().uuid()
});

/**
 * Creating a custom permission set.
 *
 * `isSystem`, `key` and `position` are ABSENT ON PURPOSE and are not oversights:
 * a caller-created group is a custom group by definition (`isSystem = false`,
 * `key = null` — the database enforces that biconditional), and `position` is
 * assigned by the server so two concurrent creates cannot claim one slot.
 * Accepting any of the three would let a client mint a row the product treats
 * as code-owned.
 *
 * `capabilities` and `surfaces` both default to empty, and both empties mean
 * "grants nothing". That is the safe direction in both cases and it is the only
 * reading available: `surfaces` is a strict allow-list, so an omitted surface
 * list must never be read as every surface.
 */
export const PermissionSetCreateBodySchema = z.object({
  name: PermissionSetNameSchema,
  resourceRelation: PermissionSetResourceRelationSchema.default(
    DEFAULT_PERMISSION_SET_RESOURCE_RELATION
  ),
  capabilities: z.array(RoleCapabilitySchema).default([]),
  surfaces: z.array(PermissionSetSurfaceValueSchema).default([])
});

/**
 * Editing a custom permission set. Every field is optional, and each ARRAY is a
 * FULL REPLACEMENT when present.
 *
 * 🚨 `undefined` AND `null` MEAN DIFFERENT THINGS ON `resourceRelation`, which
 * is the one field where that distinction is load-bearing. Omitting the key
 * leaves the stored relation alone; sending `null` clears it, turning the group
 * capability-only. Collapsing the two — `.optional()` over a non-nullable
 * enum, or a `?? null` in the handler — makes "leave it" and "revoke all
 * resource access" the same request, and only one of them is recoverable by the
 * author noticing.
 *
 * A SYSTEM GROUP IS REFUSED HERE ENTIRELY (409), not silently ignored: its
 * capability set is owned by code, and only its MEMBERSHIP is editable. A
 * partial write that dropped the forbidden fields would answer 200 having done
 * something other than what was asked.
 */
export const PermissionSetUpdateBodySchema = z
  .object({
    name: PermissionSetNameSchema.optional(),
    resourceRelation: PermissionSetResourceRelationSchema.optional(),
    capabilities: z.array(RoleCapabilitySchema).optional(),
    surfaces: z.array(PermissionSetSurfaceValueSchema).optional()
  })
  .refine(
    (body) => Object.values(body).some((value) => value !== undefined),
    "An update must change at least one field"
  );

/** Deleting a permission set is idempotent — `removed` says whether a row went. */
export const PermissionSetDeleteResponseSchema = z.object({
  removed: z.boolean()
});

/**
 * ── PERMISSION-SET MEMBERSHIP — who actually holds the set's capabilities ────
 *
 * 🚨 A DIFFERENT AUTHORITY FROM EVERY OTHER ROUTE IN THIS BLOCK, AND THAT IS THE
 * ONE THING TO READ HERE. The four routes above define what a bundle GRANTS and
 * are gated `GROUP_MANAGE`. These three decide WHO HOLDS it and are gated
 * `TEAM_MANAGE`, exactly as `/api/roles/:id/members` is and exactly as their
 * public-v1 twins are. Seating
 * somebody is a membership act that happens to be addressed by a permission-set
 * id; it is not an edit to the set.
 *
 * THE SUBJECT IS ALWAYS A USER ON THIS ROUTE. `RoleGroupMember` is polymorphic —
 * a `group` and an `organization` subject are both legal rows and the seeder
 * writes them — but neither can be named by a `userId`, so this surface reaches
 * the `user` subject only. A route for the other two would take a different body
 * and is deliberately not spelled here.
 */

/**
 * A roster read is a 404 on an unknown set, NEVER an empty list.
 *
 * A set with no members is a legitimate state — it is exactly what a create
 * produces — so `[]` cannot separate "no such set" from "the set is empty".
 * `ListPermissionSetMembersUseCase` resolves the set first for that reason.
 */
export const PermissionSetMembersListResponseSchema = z.object({
  members: z.array(PermissionSetMemberSchema)
});

/**
 * The Role, the permission set, and the user, all named on the path.
 *
 * `userId` IS A BARE STRING AND MUST STAY ONE — it is a Clerk id (`user_…`), not
 * a UUID, and `RoleMemberPathVarsSchema` already spells it that way. One name
 * under two verdicts inside one manifest is what
 * `id-validation-consistency.test.ts` rule 2 refuses by name.
 *
 * `roleGroupId` keeps the `.uuid()` its siblings carry, for the reason
 * `PermissionSetPathVarsSchema` states: an unchecked value reaches Prisma as `P2023`
 * and the server is blamed for the caller's typo.
 */
export const PermissionSetMemberPathVarsSchema = z.object({
  id: z.string().uuid(),
  roleGroupId: z.string().uuid(),
  userId: z.string()
});

/** Seating one user. The subject type is not on the wire — this route is `user` only. */
export const PermissionSetMemberAddBodySchema = z.object({
  userId: z.string().min(1)
});

/**
 * `added` says whether a row was actually written.
 *
 * IDEMPOTENT, AND `false` IS A SUCCESS — a second add of somebody already in the
 * set is the state the caller asked for. The database decides it (`createMany`
 * with `skipDuplicates` over `@@unique([roleGroupId, subjectType, subjectId])`)
 * rather than a read-then-write two concurrent adds would both pass.
 */
export const PermissionSetMemberAddResponseSchema = z.object({
  added: z.boolean()
});

/** Removing a member is idempotent — `removed` says whether a row actually went. */
export const PermissionSetMemberRemoveResponseSchema = z.object({
  removed: z.boolean()
});

/**
 * WHAT THE CALLING USER HOLDS INSIDE ONE ROLE — the read a client needs before
 * it can decide whether to draw a write control.
 *
 * ── WHY THIS IS ITS OWN ENDPOINT AND NOT A FIELD ON AN EXISTING VIEW ────────
 *
 * Every other Roles response describes THE ROLE and is therefore identical for
 * every viewer. This one describes THE VIEWER, and the two cannot share a
 * schema:
 *
 *  - `RoleCoverageViewSchema` is re-exported verbatim as the public API's
 *    `RoleCoverageV1ResponseSchema`, and that surface is reached by an API KEY,
 *    which resolves with no role grants and `isOrgAdmin: false` unconditionally.
 *    A per-viewer field there would be permanently false on a published
 *    contract.
 *  - The same schema's producer, `evaluateRoleCoverage`, also feeds the
 *    ORGANISATION ROLLUP — one caller, many Roles — where a single per-viewer
 *    answer has no meaning at all.
 *
 * ── IT REPORTS THE CAPABILITY LAYER, AND ONLY THAT LAYER ───────────────────
 *
 * 🚨 A capability appearing here says `RoleCapabilityGuard` would not refuse
 * the caller. It does NOT say the route will succeed. A Role-scoped route may
 * additionally sit behind `OrgAdminGuard`, an approval queue (`role.delete`
 * routes through one) or a use-case-level identity check (ownership transfer is
 * one, and is deliberately absent from the catalog). A client using this to HIDE
 * a control it should not offer is using it correctly; a client using it to
 * PROMISE that a write will land is not.
 *
 * ── THE ARRAY IS THE CONTRACT. NEVER A TIER, NEVER A BOOLEAN PER SCREEN ────
 *
 * The set a caller holds is the union of their permission sets in this Role,
 * and a CUSTOM group may hold any subset of the catalog — so nothing about it
 * is derivable from a membership tier, from `MEMBER_CAPABILITIES` (which is
 * module-private and COMPUTED) or from what a seeded template happens to grant
 * today. A client asks `capabilities.includes(...)`, and a capability added to
 * the catalog joins this response by construction.
 *
 * The array is the CALLER'S EFFECTIVE set: an org admin and the Role's owner
 * both hold every capability in the catalog and are reported as holding every
 * one, rather than as the empty set their grant rows would give.
 */
export const RoleCallerCapabilitiesResponseSchema = z.object({
  /** Echoed back so a cached response cannot be read against the wrong Role. */
  roleId: z.string(),
  /**
   * Every capability the caller effectively holds in this Role. Unordered, and
   * a client must not depend on the order.
   */
  capabilities: z.array(RoleCapabilitySchema)
});

/**
 * The same question with NO Role named: what the caller holds ACROSS the
 * organisation.
 *
 * ── WHY IT IS NOT `RoleCallerCapabilitiesResponseSchema` WITH A NULLABLE id ──
 *
 * That schema's `roleId` exists so a cached response cannot be read against the
 * wrong Role. There is no Role here to be wrong about, so a `roleId: null` field
 * would be a key every client has to test and no client can use — and a nullable
 * discriminant on a per-viewer response is exactly the shape that gets read as
 * "the Role this happens to be about" by the next reader. Two schemas, each
 * total.
 *
 * ── WHAT AN ORG-SCOPED CAPABILITY IS ────────────────────────────────────────
 *
 * A grant on `RoleCapabilityGrant` reaches every Role in the organisation,
 * including Roles that do not exist yet — which is the only way a capability
 * that names no Role can be held at all. `ORG_SCOPED_ONLY_CAPABILITIES` in
 * `role-capability.types.ts` is the subset that can ONLY be held this way;
 * `role.create` is the live member, because a Role being created has no id to
 * hang a per-Role permission set from.
 *
 * 🚨 THIS IS NOT A SUBSET OF THE ROLE-SCOPED ANSWER AND IT IS NOT A SUPERSET.
 * A capability held through one Role's permission set appears in THAT Role's
 * response and not here; one granted org-wide appears here and in every Role's
 * response. So a client must read whichever scope its control actually needs,
 * and must not substitute one for the other. The rule of thumb is the route: if
 * the write's path carries a `:id`, ask the Role-scoped read.
 *
 * ── IT REPORTS THE CAPABILITY LAYER, AND ONLY THAT LAYER ────────────────────
 *
 * Same boundary its Role-scoped twin draws, and one extra clause that bites
 * hardest here: `POST /api/roles` routes through the role-management APPROVAL
 * QUEUE after the capability guard has allowed it, so a caller holding
 * `role.create` may still have their create filed for review rather than
 * performed. A client HIDING the New Role control on the absence of
 * `role.create` is using this correctly; a client PROMISING the Role will exist
 * on its presence is not.
 *
 * The array is the CALLER'S EFFECTIVE set: an org admin holds the whole catalog
 * and is reported as holding every one of it, rather than as the empty set their
 * grant rows would give.
 */
export const OrgRoleCallerCapabilitiesResponseSchema = z.object({
  /**
   * Every capability the caller effectively holds across this organisation,
   * naming no Role. Unordered, and a client must not depend on the order.
   */
  capabilities: z.array(RoleCapabilitySchema)
});
